import type { World } from './world.js';
import type { EventQueue } from './events.js';
import type { Prng } from '../prng.js';
import { randRange } from '../prng.js';

const HIT_COLOR = 0xffddaa;
const EXPLOSION_COLOR = 0xff6644;
const BOMB_COLOR = 0x88ccff;

export function emitHitBurst(
  world: World,
  pos: { x: number; y: number },
  rng: Prng,
  count = 6
): void {
  for (let i = 0; i < count; i++) {
    const angle = randRange(rng, 0, Math.PI * 2);
    const speed = randRange(rng, 30, 120);
    world.spawnParticle({
      pos,
      vel: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
      lifeMs: 260,
      color: HIT_COLOR,
      size: 2,
    });
  }
}

export function emitExplosionBurst(
  world: World,
  pos: { x: number; y: number },
  rng: Prng,
  count = 20
): void {
  for (let i = 0; i < count; i++) {
    const angle = randRange(rng, 0, Math.PI * 2);
    const speed = randRange(rng, 60, 220);
    world.spawnParticle({
      pos,
      vel: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
      lifeMs: randRange(rng, 320, 520),
      color: EXPLOSION_COLOR,
      size: 3,
    });
  }
}

export function emitBombRing(world: World, pos: { x: number; y: number }, rng: Prng): void {
  const count = 48;
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count;
    const speed = randRange(rng, 200, 320);
    world.spawnParticle({
      pos,
      vel: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
      lifeMs: 600,
      color: BOMB_COLOR,
      size: 4,
    });
  }
}

/**
 * Apply smart bomb effects. Deletes all enemy bullets, damages all on-screen
 * enemies, and triggers juice.
 */
export function applyBomb(
  world: World,
  events: EventQueue,
  rng: Prng
): void {
  // Pick player for origin + bomb count decrement.
  let player = null as null | import('./entities.js').Player;
  for (const p of world.players) {
    if (p.alive && p.respawnMs === 0) {
      player = p;
      break;
    }
  }
  if (!player) return;
  if (player.bombs <= 0) return;
  player.bombs -= 1;

  // Delete all enemy bullets.
  for (const b of world.bullets) {
    if (b.alive && b.team === 'enemy') b.alive = false;
  }

  // Damage all on-screen enemies (999 = wipe).
  for (const e of world.enemies) {
    if (!e.alive) continue;
    if (e.pos.y < 0 || e.pos.y > world.worldHeight) continue;
    e.hp -= 999;
    if (e.hp <= 0) {
      e.alive = false;
      // Score from bomb — award via simple helper.
      player.score += e.scoreValue;
      if (player.score > world.hiScore) world.hiScore = player.score;
    }
  }

  // Damage boss mildly if present.
  if (world.boss && world.boss.alive) {
    world.boss.hp -= 20;
    world.boss.flashMs = 120;
  }

  emitBombRing(world, { x: player.pos.x, y: player.pos.y }, rng);
  world.shake = Math.max(world.shake, 15);
  world.hitStopMs = Math.max(world.hitStopMs, 80);
  events.push({ type: 'bomb', pos: { x: player.pos.x, y: player.pos.y } });
}
